import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react';
import { useRxCollection } from 'rxdb/plugins/react';
import type {
    TranslationDocType,
    TranslationOwnerType
} from '@ast-data/shared';
import { useDocs } from '../media/useOwnerMedia';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { translationId, type TranslationContent } from './translationState';

/**
 * The translations of ONE record's long texts while its form is open.
 *
 * Changes are drafts, like every other edit in the form: Save writes them
 * (`commit`, with the record's id — a new record has none before), Abbrechen
 * drops them, and they count towards "unsaved changes". The one exception is
 * a form open for READING: there is nothing to save, and a reader's machine
 * translation is written straight away so the next reader finds it.
 */
export interface RecordTranslations {
    ownerType: TranslationOwnerType;
    /** Undefined while a new record has not been saved yet. */
    ownerId: string | undefined;
    projectId: string;
    readOnly: boolean;
    /** The field's translation as the form currently holds it. */
    get(field: string): TranslationContent | null;
    /** Replace the field's translation; null discards it. */
    set(field: string, content: TranslationContent | null): Promise<void>;
    isDirty: boolean;
    /** Write the drafts for the record saved under `ownerId`. */
    commit(ownerId: string): Promise<void>;
}

/** Provided by the form frame; null where a form keeps no translations. */
export const RecordTranslationsContext =
    createContext<RecordTranslations | null>(null);

/** The open record's translations, or null outside a translating form. */
export function useRecordTranslationsScope(): RecordTranslations | null {
    return useContext(RecordTranslationsContext);
}

function contentOf(doc: TranslationDocType): TranslationContent {
    return {
        sourceLang: doc.sourceLang,
        targetLang: doc.targetLang,
        sourceText: doc.sourceText,
        text: doc.text,
        origin: doc.origin,
        approvedBy: doc.approvedBy,
        approvedAt: doc.approvedAt
    };
}

/** A field with no draft is absent from the map; a discarded one maps to null. */
type Drafts = ReadonlyMap<string, TranslationContent | null>;

export function useRecordTranslations({
    ownerType,
    ownerId,
    projectId,
    readOnly = false,
    immediate = false
}: {
    ownerType: TranslationOwnerType;
    ownerId: string | undefined;
    projectId: string;
    readOnly?: boolean;
    /**
     * Write every change straight away, for a form that saves itself (the
     * project's Metadaten). Only once the record exists; before, it drafts.
     */
    immediate?: boolean;
}): RecordTranslations {
    const collection = useRxCollection<TranslationDocType>('translations');
    const docs = useDocs<TranslationDocType>('translations', {
        ownerId: ownerId ?? ''
    });
    const [drafts, setDrafts] = useState<Drafts>(() => new Map());

    // Kept as long as the family itself does not change: the projects query
    // reports every edit of any project, and each would re-render the form.
    const familyIds = useFamilyProjectIds(projectId);
    const familyKey = familyIds.join('\u0000');
    const family = useMemo(() => familyKey.split('\u0000'), [familyKey]);

    /**
     * Only translations filed under this record's type AND its project's
     * family count. The id is derived from the owner, so anyone could write
     * one for any record; one that claims a project outside the family is not
     * this record's translation. Within the family it is: a shared Befund is
     * opened from any Teilmaßnahme, and its translation is filed under the one
     * it was written from.
     */
    const stored = useMemo(() => {
        const inFamily = new Set(family);
        const byField = new Map<string, TranslationContent>();
        for (const doc of docs) {
            if (doc.ownerType === ownerType && inFamily.has(doc.projectId)) {
                byField.set(doc.field, contentOf(doc));
            }
        }
        return byField;
    }, [docs, ownerType, family]);

    const write = useCallback(
        async (
            targetOwnerId: string,
            field: string,
            content: TranslationContent | null
        ) => {
            if (!collection) {
                return;
            }
            const id = translationId(targetOwnerId, field);
            const existing = await collection.findOne(id).exec();
            const now = new Date().toISOString();
            // A project's own texts are filed under the project itself — which
            // a project being created does not know before its insert.
            const docProjectId =
                ownerType === 'project' ? targetOwnerId : projectId;
            if (content === null) {
                await existing?.remove();
                return;
            }
            if (existing) {
                // Filed in the family, it stays where it is: moving it would
                // check the writer's rights in a project they did not open.
                // Filed outside (a record moved since), it comes home.
                await existing.incrementalPatch({
                    ...content,
                    ...(family.includes(existing.projectId)
                        ? {}
                        : { projectId: docProjectId }),
                    updatedAt: now
                });
                return;
            }
            await collection.insert({
                id,
                projectId: docProjectId,
                ownerType,
                ownerId: targetOwnerId,
                field,
                ...content,
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            });
        },
        [collection, ownerType, projectId, family]
    );

    const get = useCallback(
        (field: string) =>
            drafts.has(field)
                ? (drafts.get(field) ?? null)
                : (stored.get(field) ?? null),
        [drafts, stored]
    );

    const set = useCallback(
        async (field: string, content: TranslationContent | null) => {
            if (readOnly || (immediate && ownerId)) {
                if (ownerId) {
                    await write(ownerId, field, content);
                }
                return;
            }
            setDrafts((previous) => new Map(previous).set(field, content));
        },
        [readOnly, immediate, ownerId, write]
    );

    const commit = useCallback(
        async (savedOwnerId: string) => {
            for (const [field, content] of drafts) {
                await write(savedOwnerId, field, content);
            }
            setDrafts(new Map());
        },
        [drafts, write]
    );

    return useMemo(
        () => ({
            ownerType,
            ownerId,
            projectId,
            readOnly,
            get,
            set,
            isDirty: drafts.size > 0,
            commit
        }),
        [ownerType, ownerId, projectId, readOnly, get, set, drafts, commit]
    );
}
